import * as L from 'leaflet';

/**
 * Is this point inside this ring? Ray casting, counting crossings.
 *
 * A point exactly ON the outline is deliberately undefined: for a square,
 * ray casting reports it inside along two of the four sides and outside along
 * the others, and no convention makes that meaningful. Callers asking "do these
 * overlap" must exclude the boundary themselves, which is what `pullOutOf`
 * does: a corner sitting on a neighbour's edge is a SHARED corner, the thing
 * snapping exists to produce, and calling it an overlap would have the tool
 * fighting its own seams.
 *
 * Lives here rather than with the boundary editor because the map layer needs
 * it too, and a layer may not import from a page. `boundary-snapping`
 * re-exports it, so everything that already asked that module for it still can.
 */
export function pointInRing(point: L.LatLng, ring: readonly L.LatLng[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i];
    const b = ring[j];
    const straddles = (a.lat > point.lat) !== (b.lat > point.lat);
    if (!straddles) continue;
    const crossing = (b.lng - a.lng) * (point.lat - a.lat) / (b.lat - a.lat) + a.lng;
    if (point.lng < crossing) inside = !inside;
  }
  return inside;
}

/** Where a label goes, and how much room it has there. */
export interface LabelAnchor {
  at: L.LatLng;
  /** Distance from `at` to the nearest edge, in degrees of latitude — the
   *  radius of the empty circle the label has to sit in. */
  clearance: number;
}

/**
 * The roomiest point inside a shape: the one furthest from any edge.
 *
 * NOT the centroid, which is the obvious choice and the wrong one. A territory
 * that wraps a lake or bends around a highway is concave, and its centroid can
 * sit outside the shape entirely — the label would name a patch it is not
 * standing on, which is worse than no label. This is the pole of
 * inaccessibility: the centre of the largest circle that fits inside.
 *
 * Found by grid search rather than by the usual quadtree: a territory has tens
 * of corners and a page has tens of territories, so two coarse passes cost
 * microseconds and are simpler to read than a priority queue. The second pass
 * looks again around the winner, which is what turns "roughly the middle" into
 * a point that is actually centred in the widest part.
 *
 * The clearance comes back with it because the caller has to decide whether
 * the label FITS: a patch two streets wide has a roomiest point like any
 * other, and a name written across it would cross both its edges.
 */
export function labelAnchor(
  rings: readonly (readonly L.LatLng[])[],
): LabelAnchor | null {
  // The outer ring is the one to label. Largest by bounding box rather than by
  // signed area: holes are wound the other way and a donut's hole must not win.
  let ring: readonly L.LatLng[] | null = null;
  let widest = -1;
  for (const candidate of rings) {
    if (candidate.length < 3) continue;
    const box = extent(candidate);
    const size = (box.maxLat - box.minLat) * (box.maxLng - box.minLng);
    if (size > widest) { widest = size; ring = candidate; }
  }
  if (!ring) return null;

  const box = extent(ring);
  // Longitude is narrower than latitude away from the equator, and a label
  // sits in screen space; without this a tall thin patch scores as roomy.
  const lngScale = Math.cos(((box.minLat + box.maxLat) / 2) * Math.PI / 180) || 1;

  let best: LabelAnchor | null = null;
  let minLat = box.minLat, maxLat = box.maxLat;
  let minLng = box.minLng, maxLng = box.maxLng;

  for (let pass = 0; pass < 2; pass++) {
    const steps = 12;
    const latStep = (maxLat - minLat) / (steps + 1);
    const lngStep = (maxLng - minLng) / (steps + 1);
    if (latStep <= 0 || lngStep <= 0) break;

    for (let i = 1; i <= steps; i++) {
      for (let j = 1; j <= steps; j++) {
        const point = L.latLng(minLat + latStep * i, minLng + lngStep * j);
        if (!pointInRing(point, ring)) continue;
        const clearance = distanceToRing(point, ring, lngScale);
        if (!best || clearance > best.clearance) best = { at: point, clearance };
      }
    }

    if (!best) return null;
    // Look again, closer in, around the best point so far.
    minLat = best.at.lat - latStep; maxLat = best.at.lat + latStep;
    minLng = best.at.lng - lngStep; maxLng = best.at.lng + lngStep;
  }

  return best;
}

function extent(ring: readonly L.LatLng[]) {
  let minLat = Infinity, maxLat = -Infinity, minLng = Infinity, maxLng = -Infinity;
  for (const p of ring) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
  }
  return { minLat, maxLat, minLng, maxLng };
}

/** Shortest distance from a point to the ring's outline, in degrees of
 *  latitude, with longitude scaled so the two axes are comparable. */
function distanceToRing(
  point: L.LatLng,
  ring: readonly L.LatLng[],
  lngScale: number,
): number {
  let best = Infinity;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const d = distanceToSegment(point, ring[j], ring[i], lngScale);
    if (d < best) best = d;
  }
  return best;
}

function distanceToSegment(
  p: L.LatLng, a: L.LatLng, b: L.LatLng, lngScale: number,
): number {
  const px = (p.lng - a.lng) * lngScale, py = p.lat - a.lat;
  const bx = (b.lng - a.lng) * lngScale, by = b.lat - a.lat;
  const lengthSq = bx * bx + by * by;
  const t = lengthSq === 0 ? 0 : Math.max(0, Math.min(1, (px * bx + py * by) / lengthSq));
  const dx = px - bx * t, dy = py - by * t;
  return Math.sqrt(dx * dx + dy * dy);
}
